/**
 * macro-release-series — the macro store's raw series → the releases tiles'
 * readings, in display units. Pure: `api/macro-releases.ts` fetches, this maps.
 *
 * The route is qv-data's admin-only `GET /macro/series?ids=&since=`
 * (`services/qv-data/app/routers/macro_series.py`), which serves every stored
 * `macro_store` series **as stored**, so the arithmetic a tile needs is here:
 *
 * | tile | from | done here |
 * |---|---|---|
 * | `cpi` | `us.inflation.cpi` (an index) | year over year — `cpi_year_over_year` stopped in Apr 2025 |
 * | `corePce` | `us.inflation.pce_core` (an index) | year over year |
 * | `breakeven5y` | `us.expectations.market_5_year` | — |
 * | `unemployment` | `us.labor.unemployment_rate` | — |
 * | `jolts` | `us.labor.job_openings` (thousands) | ÷ 1000 → M |
 * | `ahe` | `us.labor.avg_hourly_earnings` (dollars) | year over year |
 * | `fedFunds` | `us.funding.fed_funds_target_lower` / `_upper` (daily) | the last day of each month, as `{ low, value }` |
 * | `gdp` | `us.fred.a191rl1q225sbea` (quarterly, % ann.) | — |
 * | `retail` | `us.fred.rsafs` | month over month |
 * | `indpro` | `us.fred.indpro` (an index) | year over year |
 * | `ppi` | `us.fred.ppifis` (an index) | month over month |
 * | `payrolls` | `us.fred.payems` (thousands) | the month's change, in K |
 * | `claims` | `us.fred.icsa` (weekly, persons) | ÷ 1000 → K |
 * | `mortgage30y` | `us.fred.mortgage30us` (weekly %) | — |
 * | `umich` | `us.fred.umcsent` | — |
 * | `ism` | `us.calendar.ism_manufacturing` (the calendar's actuals, dated the release day) | — |
 * | `confBoard` | `us.calendar.cb_consumer_confidence` (likewise) | — |
 * | `recession` | `us.fred.recprousm156n` (%) | — |
 * | `realYield10y` | `us.treasury.yield_10_year` (daily) − `us.expectations.market_10_year` (monthly) | the month's mean yield minus that month's breakeven |
 *
 * FRED ids are `us.fred.<lowercase FRED id>`. A weekly tile's 12 columns are its
 * last 12 weeks; a `us.calendar.*` tile's are its last 12 releases. A tile
 * whose spec is `pending` asks for nothing. An id
 * the store lacks comes back with no points and its tile says "No readings yet".
 */

import type { ReleaseData, ReleasePoint } from "./macro-releases";

/** One series as the route answers it: `[YYYY-MM-DD, value]`, oldest first. */
export interface MacroSeries {
  id: string;
  points: readonly (readonly [string, number | null])[];
}

export interface MacroSeriesResponse {
  series: readonly MacroSeries[];
  missing?: readonly string[];
  builtAt?: string | null;
  since?: string | null;
}

export const SERIES = {
  cpi: "us.inflation.cpi",
  pceCore: "us.inflation.pce_core",
  breakeven5y: "us.expectations.market_5_year",
  breakeven10y: "us.expectations.market_10_year",
  yield10y: "us.treasury.yield_10_year",
  unemployment: "us.labor.unemployment_rate",
  jobOpenings: "us.labor.job_openings",
  hourlyEarnings: "us.labor.avg_hourly_earnings",
  fedLower: "us.funding.fed_funds_target_lower",
  fedUpper: "us.funding.fed_funds_target_upper",
  gdp: "us.fred.a191rl1q225sbea",
  retail: "us.fred.rsafs",
  indpro: "us.fred.indpro",
  ppi: "us.fred.ppifis",
  payrolls: "us.fred.payems",
  claims: "us.fred.icsa",
  mortgage30y: "us.fred.mortgage30us",
  umich: "us.fred.umcsent",
  recession: "us.fred.recprousm156n",
  ism: "us.calendar.ism_manufacturing",
  confBoard: "us.calendar.cb_consumer_confidence",
} as const;

/** Every id the section asks for, in one request. */
export const RELEASE_SERIES_IDS: readonly string[] = Object.values(SERIES);

/**
 * How far back to ask: 12 columns, the reading before the first, and 12 months
 * before that for a year-over-year — 25 months, rounded up to three years.
 */
export const RELEASE_LOOKBACK_YEARS = 3;

/** `since` for a request made on `today` (YYYY-MM-DD in, YYYY-MM-DD out). */
export function releaseSince(today: string): string {
  const y = Number(today.slice(0, 4)) - RELEASE_LOOKBACK_YEARS;
  return `${y}${today.slice(4, 7)}-01`;
}

type Points = ReleasePoint[];

function clean(s: MacroSeries | undefined): Points {
  if (!s) return [];
  const out: Points = [];
  for (const [date, value] of s.points) {
    if (typeof value === "number" && Number.isFinite(value)) out.push({ date, value });
  }
  return out;
}

const monthKey = (date: string) => date.slice(0, 7);

/** The month 12 before `YYYY-MM`. */
const yearBefore = (key: string) => `${Number(key.slice(0, 4)) - 1}${key.slice(4)}`;

/** Percent change on the same month a year earlier; a month without one is dropped. */
export function yearOverYear(points: readonly ReleasePoint[]): Points {
  const byMonth = new Map(points.map((p) => [monthKey(p.date), p.value]));
  const out: Points = [];
  for (const p of points) {
    const prior = byMonth.get(yearBefore(monthKey(p.date)));
    if (prior !== undefined && prior > 0) out.push({ date: p.date, value: (p.value / prior - 1) * 100 });
  }
  return out;
}

/** The month before `YYYY-MM`. */
const monthBefore = (key: string) => {
  const t = Number(key.slice(0, 4)) * 12 + Number(key.slice(5, 7)) - 2;
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, "0")}`;
};

/** Percent change on the month before; a month without one is dropped. */
export function monthOverMonth(points: readonly ReleasePoint[]): Points {
  const byMonth = new Map(points.map((p) => [monthKey(p.date), p.value]));
  const out: Points = [];
  for (const p of points) {
    const prior = byMonth.get(monthBefore(monthKey(p.date)));
    if (prior !== undefined && prior > 0) out.push({ date: p.date, value: (p.value / prior - 1) * 100 });
  }
  return out;
}

/** The difference from the month before, in the series' own unit; a month without one is dropped. */
export function monthChange(points: readonly ReleasePoint[]): Points {
  const byMonth = new Map(points.map((p) => [monthKey(p.date), p.value]));
  const out: Points = [];
  for (const p of points) {
    const prior = byMonth.get(monthBefore(monthKey(p.date)));
    if (prior !== undefined) out.push({ date: p.date, value: p.value - prior });
  }
  return out;
}

/** The last reading of each month, dated the 1st. Input oldest first. */
export function monthEnd(points: readonly ReleasePoint[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const p of points) out.set(monthKey(p.date), p.value);
  return out;
}

/** The mean reading of each month. */
export function monthMean(points: readonly ReleasePoint[]): Map<string, number> {
  const sum = new Map<string, [number, number]>();
  for (const p of points) {
    const k = monthKey(p.date);
    const s = sum.get(k) ?? [0, 0];
    sum.set(k, [s[0] + p.value, s[1] + 1]);
  }
  return new Map([...sum].map(([k, [t, n]]) => [k, t / n]));
}

/** The response → readings per tile id, in display units. */
export function releasesFromSeries(resp: MacroSeriesResponse): ReleaseData {
  const by = new Map(resp.series.map((s) => [s.id, s]));
  const get = (id: string) => clean(by.get(id));

  const lower = monthEnd(get(SERIES.fedLower));
  const fedFunds: Points = [...monthEnd(get(SERIES.fedUpper))]
    .filter(([k]) => lower.has(k))
    .map(([k, value]) => ({ date: `${k}-01`, value, low: lower.get(k)! }));

  const nominal = monthMean(get(SERIES.yield10y));
  const realYield10y: Points = get(SERIES.breakeven10y)
    .filter((p) => nominal.has(monthKey(p.date)))
    .map((p) => ({ date: `${monthKey(p.date)}-01`, value: nominal.get(monthKey(p.date))! - p.value }));

  const data: ReleaseData = {
    cpi: yearOverYear(get(SERIES.cpi)),
    corePce: yearOverYear(get(SERIES.pceCore)),
    breakeven5y: get(SERIES.breakeven5y),
    unemployment: get(SERIES.unemployment),
    jolts: get(SERIES.jobOpenings).map((p) => ({ ...p, value: p.value / 1000 })),
    ahe: yearOverYear(get(SERIES.hourlyEarnings)),
    fedFunds,
    realYield10y,
    gdp: get(SERIES.gdp),
    retail: monthOverMonth(get(SERIES.retail)),
    indpro: yearOverYear(get(SERIES.indpro)),
    ppi: monthOverMonth(get(SERIES.ppi)),
    payrolls: monthChange(get(SERIES.payrolls)),
    claims: get(SERIES.claims).map((p) => ({ ...p, value: p.value / 1000 })),
    mortgage30y: get(SERIES.mortgage30y),
    umich: get(SERIES.umich),
    recession: get(SERIES.recession),
    ism: get(SERIES.ism),
    confBoard: get(SERIES.confBoard),
  };
  for (const k of Object.keys(data)) if (data[k]!.length === 0) delete data[k];
  return data;
}
